import {DomainObject, oneOf} from 'shared/store';
import { observable, computed, makeObservable } from 'mobx';
import {Dependent, OptionalCoverageEnrolment} from 'main/stores/benefits';
import {calendarDate} from 'shared/tools';
import BeneficiaryForm from './BeneficiaryForm';

class BenefitInfo extends DomainObject {
  employeeId;
  gender;
  hasSpouse;
  healthCoverage;
  dentalCoverage;
  spouseFirstName;
  spouseLastName;
  spousalGender;
  spousalRelationship;
  spouseBirthDate;
  spouseMarriageDate;
  spousalHealthCoverage;
  spousalDentalCoverage;
  spousalVisionCoverage;
  spouseInsurerName;
  spousePolicyNumber;
  dependents;
  hasCanadianVisaOrPermit;
  visaOrPermitExpiryDate;
  hasProvincialHealthPlan;
  optionalCoverageEnrolments;
  healthCoverageOptions;
  dentalCoverageOptions;
  healthCoverageHelperText;
  dentalCoverageHelperText;
  languagePreference;
  taxExempt;
  inacNumber;
  canMakeChangeRequest = false;
  beneficiaryForms;

  _relations = {
    dependents: [Dependent],
    optionalCoverageEnrolments: [OptionalCoverageEnrolment],
    gender: oneOf(['male', 'female', 'prefer_not_to_say']),
    spousalGender: oneOf(['male', 'female']),
    healthCoverage: oneOf(['family', 'single', 'waived', 'single_parent', 'couple']),
    dentalCoverage: oneOf(['family', 'single', 'waived', 'single_parent', 'couple']),
    spousalHealthCoverage: oneOf(['family', 'single', 'waived', 'none']),
    spousalDentalCoverage: oneOf(['family', 'single', 'waived', 'none']),
    spousalVisionCoverage: oneOf(['family', 'single', 'waived', 'none']),
    beneficiaryForms: [BeneficiaryForm],
    _dates: ['visaOrPermitExpiryDate', 'spouseBirthDate', 'spouseMarriageDate']
  };

  constructor(other) {
    super();

    makeObservable(this, {
      employeeId: observable,
      gender: observable,
      hasSpouse: observable,
      healthCoverage: observable,
      dentalCoverage: observable,
      spouseFirstName: observable,
      spouseLastName: observable,
      spousalGender: observable,
      spousalRelationship: observable,
      spouseBirthDate: observable,
      spouseMarriageDate: observable,
      spousalHealthCoverage: observable,
      spousalDentalCoverage: observable,
      spousalVisionCoverage: observable,
      spouseInsurerName: observable,
      spousePolicyNumber: observable,
      dependents: observable,
      hasCanadianVisaOrPermit: observable,
      visaOrPermitExpiryDate: observable,
      hasProvincialHealthPlan: observable,
      optionalCoverageEnrolments: observable,
      healthCoverageOptions: observable,
      dentalCoverageOptions: observable,
      healthCoverageHelperText: observable,
      dentalCoverageHelperText: observable,
      languagePreference: observable,
      taxExempt: observable,
      inacNumber: observable,
      canMakeChangeRequest: observable,
      beneficiaryForms: observable,
      visaOrPermitExpiryDateView: computed,
      spouseBirthDateView: computed,
      spouseMarriageDateView: computed,
      spouseHasInsurer: computed
    });

    this.merge(other);
  }

  get visaOrPermitExpiryDateView() {
    return calendarDate(this.visaOrPermitExpiryDate);
  }

  get spouseBirthDateView() {
    return calendarDate(this.spouseBirthDate);
  }

  get spouseMarriageDateView() {
    return calendarDate(this.spouseMarriageDate);
  }

  get spouseHasInsurer() {
    return this.spousalHealthCoverage !== 'none' ||
      this.spousalDentalCoverage !== 'none' ||
      this.spousalVisionCoverage !== 'none';
  }
}

export default BenefitInfo;
